/**
 * ヘッダーの「自分のタスク」バッジ用: 自分が担当の未完了タスク数 (steering 627)。
 *
 * `GET /tasks` は索引駆動の cursor ページングで合計件数を返さない (`useTaskStats` と同じ事情)。
 * `scope=assigned_to_me&status=open&limit=100` を最大 {@link MY_OPEN_TASK_COUNT_MAX_PAGES} ページ
 * 追い読みして FE で数え、上限に達しても `nextCursor` が残れば `overflow=true` (表示は「99+」)。
 *
 * 更新のタイミング: {@link MY_OPEN_TASK_COUNT_REFRESH_MS} ごと / 画面 (route) を移動したとき /
 * タブへ戻ったとき (`visibilitychange`) / タスクの変更 event ({@link TASKS_CHANGED_EVENT})。
 * 取得は **同時に 1 系列** — 取得中に契機が重なっても並行に読み始めず、完了後の 1 回にまとめる
 * (一括完了で event が連続しても通信は増えない / 定期更新が進行中の集計を捨てない)。
 * 取得に失敗したときは **前の値を保つ** (0 に潰して「タスクなし」に見せない)。
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { apiFetch } from '@/api/client';
import { buildTaskListQuery, type TaskListResponse } from '@/hooks/useTasks';
import { TASKS_CHANGED_EVENT } from '@/utils/tasksChanged';

/** 1 ページの件数 (BE 上限)。 */
export const MY_OPEN_TASK_COUNT_PAGE_LIMIT = 100;
/** 追い読みするページ数の上限。バッジは 99+ で頭打ちなので 3 ページで足りる。 */
export const MY_OPEN_TASK_COUNT_MAX_PAGES = 3;
/** 定期的に読み直す間隔 (通知ベルの未読数と同じ 30 秒)。 */
export const MY_OPEN_TASK_COUNT_REFRESH_MS = 30_000;
/** バッジに数字で出す上限。超えたら「99+」。 */
export const MY_OPEN_TASK_BADGE_MAX = 99;

export interface MyOpenTaskCount {
  /** 自分が担当の未完了タスク数。まだ読めていなければ null。 */
  count: number | null;
  /** 追い読みの上限に達し、`count` が全件ではない。 */
  overflow: boolean;
  /** 読み直す (取得中なら完了後の 1 回にまとめる)。 */
  refetch: () => void;
}

interface CountState {
  count: number | null;
  overflow: boolean;
}

/** 件数用の一覧 path。`cursor` は opaque のままつなぐ。 */
export function myOpenTaskCountPagePath(cursor?: string): string {
  return `/tasks${buildTaskListQuery({
    scope: 'assigned_to_me',
    status: 'open',
    limit: MY_OPEN_TASK_COUNT_PAGE_LIMIT,
    ...(cursor ? { cursor } : {}),
  })}`;
}

/**
 * 追い読みして数える。途中で `isActive()` が偽になったら (unmount / 無効化) それ以上のページを
 * 読まずに null を返す。
 */
async function countOpenTasks(isActive: () => boolean): Promise<CountState | null> {
  let count = 0;
  let cursor: string | undefined;
  let overflow = false;
  for (let page = 0; ; page += 1) {
    const res = await apiFetch<TaskListResponse>(myOpenTaskCountPagePath(cursor));
    if (!isActive()) return null;
    count += res.tasks?.length ?? 0;
    cursor = res.nextCursor ?? undefined;
    if (!cursor) break;
    if (page + 1 >= MY_OPEN_TASK_COUNT_MAX_PAGES) {
      overflow = true;
      break;
    }
  }
  return { count, overflow };
}

/**
 * @param enabled 取得するか (`tasks:read` が無いときは false にして API を呼ばない)
 */
export function useMyOpenTaskCount(enabled: boolean): MyOpenTaskCount {
  const [state, setState] = useState<CountState>({ count: null, overflow: false });
  /** mount 中かつ enabled の間だけ真。応答を反映してよいかの判定に使う。 */
  const activeRef = useRef(false);
  /** 取得中 (1 系列だけ走らせる)。 */
  const inFlightRef = useRef(false);
  /** 取得中に来た契機。完了後に 1 回だけ読み直す。 */
  const pendingRef = useRef(false);

  const refetch = useCallback(() => {
    if (!activeRef.current) return;
    if (inFlightRef.current) {
      pendingRef.current = true;
      return;
    }
    inFlightRef.current = true;
    void (async () => {
      try {
        do {
          pendingRef.current = false;
          const next = await countOpenTasks(() => activeRef.current);
          if (!next) return;
          setState(next);
        } while (pendingRef.current && activeRef.current);
      } catch {
        // 失敗しても前の値を保つ (バッジを 0 に潰さない)。次の定期更新 / event で取り直す。
      } finally {
        inFlightRef.current = false;
      }
    })();
  }, []);

  const { pathname } = useLocation();

  // 初回と、画面 (route) の移動で読み直す (タスク画面で完了にしてから別画面へ移った直後など)。
  useEffect(() => {
    if (!enabled) return;
    activeRef.current = true;
    refetch();
    return () => {
      activeRef.current = false;
    };
  }, [enabled, pathname, refetch]);

  useEffect(() => {
    if (!enabled) return;
    const id = window.setInterval(refetch, MY_OPEN_TASK_COUNT_REFRESH_MS);
    const onVisible = () => {
      if (document.visibilityState === 'visible') refetch();
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener(TASKS_CHANGED_EVENT, refetch);
    return () => {
      window.clearInterval(id);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener(TASKS_CHANGED_EVENT, refetch);
    };
  }, [enabled, refetch]);

  return { count: state.count, overflow: state.overflow, refetch };
}
